import { BookOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import type { Title } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

const TYPE_BADGE: Record<Title['type'], string> = {
  manga: 'bg-red-100 text-red-700',
  manhwa: 'bg-blue-100 text-blue-700',
  manhua: 'bg-yellow-100 text-yellow-700',
  webtoon: 'bg-purple-100 text-purple-700',
}

const STATUS_BADGE: Record<Title['status'], string> = {
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-600',
  hiatus: 'bg-orange-100 text-orange-700',
}

function SearchContent() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 350)
  const [results, setResults] = useState<Title[]>([])
  const [newest, setNewest] = useState<Title[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()

    async function fetchNewest() {
      setIsLoading(true)
      const { data } = await supabase
        .from('titles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20)
      setNewest(data ?? [])
      setIsLoading(false)
    }

    fetchNewest()
  }, [])

  useEffect(() => {
    const trimmed = debouncedQuery.trim()
    if (!trimmed) {
      setResults([])
      return
    }

    async function search() {
      setIsLoading(true)

      // Search canonical names
      const { data: byName } = await supabase
        .from('titles')
        .select('*')
        .ilike('canonical_name', `%${trimmed}%`)
        .limit(20)

      // Search aliases and resolve title_ids not already found
      const { data: byAlias } = await supabase
        .from('aliases')
        .select('title_id')
        .ilike('name', `%${trimmed}%`)
        .limit(20)

      const foundIds = new Set(byName?.map((t) => t.id) ?? [])
      const aliasIds = (byAlias ?? [])
        .map((a) => a.title_id)
        .filter((id) => !foundIds.has(id))

      let aliasedTitles: Title[] = []
      if (aliasIds.length) {
        const { data } = await supabase
          .from('titles')
          .select('*')
          .in('id', aliasIds)
          .limit(10)
        aliasedTitles = data ?? []
      }

      setResults([...(byName ?? []), ...aliasedTitles])
      setIsLoading(false)
    }

    search()
  }, [debouncedQuery])

  return (
    <div>
      {/* Search input */}
      <div className="relative mb-8">
        <MagnifyingGlassIcon className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search titles, aliases..."
          className="w-full rounded-xl border border-gray-300 py-3 pr-4 pl-10 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      )}

      {!isLoading && query.trim() && results.length === 0 && (
        <div className="py-16 text-center text-gray-500">
          <MagnifyingGlassIcon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p>
            No titles found for "<strong>{query}</strong>"
          </p>
        </div>
      )}

      {!isLoading && !query.trim() && newest.length > 0 && (
        <>
          <p className="mb-4 font-medium text-gray-500 text-sm">
            Recently Added
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {newest.map((title) => (
              <Link
                key={title.id}
                to={`/title/${title.id}`}
                className="flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="relative h-24 w-16 shrink-0 overflow-hidden bg-gray-100">
                  {title.cover_url ? (
                    <img
                      src={title.cover_url}
                      alt={title.canonical_name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <BookOpenIcon className="h-8 w-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <p className="line-clamp-2 font-semibold text-gray-900 text-sm leading-tight">
                    {title.canonical_name}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span
                      className={`rounded px-1.5 py-0.5 font-medium text-xs capitalize ${TYPE_BADGE[title.type]}`}
                    >
                      {title.type}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 font-medium text-xs capitalize ${STATUS_BADGE[title.status]}`}
                    >
                      {title.status}
                    </span>
                  </div>
                  {title.description && (
                    <p className="mt-1 line-clamp-2 text-gray-500 text-xs">
                      {title.description}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {results.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {results.map((title) => (
            <Link
              key={title.id}
              to={`/title/${title.id}`}
              className="flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="relative h-24 w-16 shrink-0 overflow-hidden bg-gray-100">
                {title.cover_url ? (
                  <img
                    src={title.cover_url}
                    alt={title.canonical_name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <BookOpenIcon className="h-8 w-8 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-1 p-3">
                <p className="line-clamp-2 font-semibold text-gray-900 text-sm leading-tight">
                  {title.canonical_name}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <span
                    className={`rounded px-1.5 py-0.5 font-medium text-xs capitalize ${TYPE_BADGE[title.type]}`}
                  >
                    {title.type}
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 font-medium text-xs capitalize ${STATUS_BADGE[title.status]}`}
                  >
                    {title.status}
                  </span>
                </div>
                {title.description && (
                  <p className="mt-1 line-clamp-2 text-gray-500 text-xs">
                    {title.description}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default function SearchPage() {
  return (
    <UserGuard>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="mb-6 font-bold text-2xl text-gray-900">Search</h1>
        <SearchContent />
      </div>
    </UserGuard>
  )
}
